import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { FormField } from '../types/forms';
import type { FieldType } from '../types/forms';
import type { NextFieldMap, NextPageMap } from './useConditionalLogic';
import type { ResolvedPage } from '../lib/formPages';

// Field types that are not navigable (not rendered as standalone slides)
const NON_NAVIGABLE_TYPES: FieldType[] = ['welcome_screen', 'thank_you_screen', 'heading', 'paragraph'];

// Field types that support auto-advance (single-selection)
const AUTO_ADVANCE_TYPES: FieldType[] = ['radio', 'yes_no', 'rating', 'nps', 'opinion_scale'];

interface UseTypeformNavigationProps {
  fields: FormField[];
  formData: Record<string, any>;
  validateField: (field: FormField) => string | null;
  onSubmit: () => void;
  nextFieldMap?: NextFieldMap;
  calculateProgress?: (fieldId: string) => { current: number; total: number; percentage: number };
  initialIndex?: number; // For resume flow — skip to a specific navigable index
  /** Resolved pages from resolvePages(). If provided, enables page-level navigation. */
  pages?: ResolvedPage[];
  /** Called when page validation fails on a multi-field page. Receives the failing fieldId and error message. */
  onPageValidationError?: (fieldId: string, error: string) => void;
  /** Page-level branching map (Story 5.7). When provided, goNextPage consults this map. */
  nextPageMap?: NextPageMap;
  /** Page-aware progress calculator (Story 5.7). Used when pages exist and no loops detected. */
  calculatePageProgress?: (pageId: string) => { current: number; total: number; percentage: number };
}

/**
 * Índice da primeira página com ao menos um campo navegável, varrendo a partir de
 * `from` no sentido indicado. Retorna -1 quando não há nenhuma.
 *
 * resolvePages() cria uma página por campo — inclusive heading/paragraph, que não
 * geram slide próprio (são renderizados como contexto da próxima pergunta). Sem
 * pular essas páginas a navegação travava silenciosamente ao chegar num heading.
 */
function findNavigablePageIndex(
  pages: ResolvedPage[],
  from: number,
  direction: 'forward' | 'backward' = 'forward'
): number {
  const step = direction === 'backward' ? -1 : 1;
  for (let idx = from; idx >= 0 && idx < pages.length; idx += step) {
    if (pages[idx].fields.some((f) => !NON_NAVIGABLE_TYPES.includes(f.type))) return idx;
  }
  return -1;
}

interface NavigableItem {
  field: FormField;
  contextFields: FormField[]; // heading/paragraph fields that precede this input
}

interface UseTypeformNavigationReturn {
  currentIndex: number;
  currentField: FormField | null;
  currentContextFields: FormField[]; // heading/paragraph before current input
  totalQuestions: number;
  progress: number;
  isFirstQuestion: boolean;
  isLastQuestion: boolean;
  isWelcomeScreen: boolean;
  isComplete: boolean;
  goNext: () => void;
  goPrevious: () => void;
  goToIndex: (index: number) => void;
  validationError: string | null;
  clearError: () => void;
  // Page-level API
  currentPageIndex: number;
  currentPage: ResolvedPage | null;
  currentFields: FormField[];
  totalPages: number;
  pageProgress: number;
  isFirstPage: boolean;
  isLastPage: boolean;
  isMultiFieldPage: boolean;
  goNextPage: () => void;
  goPreviousPage: () => void;
  shouldAutoAdvance: (fieldType: FieldType) => boolean;
}

export function useTypeformNavigation({
  fields,
  formData,
  validateField,
  onSubmit,
  nextFieldMap,
  calculateProgress,
  initialIndex,
  pages,
  onPageValidationError,
  nextPageMap,
  calculatePageProgress,
}: UseTypeformNavigationProps): UseTypeformNavigationReturn {
  const [currentIndex, setCurrentIndex] = useState(-1); // -1 = welcome screen
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [pageHistory, setPageHistory] = useState<number[]>([]); // AC7: page index history
  const [fieldHistory, setFieldHistory] = useState<string[]>([]); // field-level history for branching

  // Separate fields into categories
  const welcomeField = useMemo(
    () => fields.find((f) => f.type === 'welcome_screen') || null,
    [fields]
  );

  // Build navigable items: each input field with optional preceding heading/paragraph context
  const navigableItems = useMemo(() => {
    const items: NavigableItem[] = [];
    let pendingContext: FormField[] = [];

    for (const field of fields) {
      if (field.type === 'welcome_screen' || field.type === 'thank_you_screen') {
        continue;
      }
      if (field.type === 'heading' || field.type === 'paragraph') {
        pendingContext.push(field);
        continue;
      }
      if (field.type === 'statement') {
        items.push({ field, contextFields: [...pendingContext] });
        pendingContext = [];
        continue;
      }
      items.push({ field, contextFields: [...pendingContext] });
      pendingContext = [];
    }

    return items;
  }, [fields]);

  const totalQuestions = navigableItems.length;

  // Initialize: use initialIndex if provided (resume flow), else welcome or 0
  useEffect(() => {
    if (initialIndex !== undefined && initialIndex >= 0) {
      setCurrentIndex(initialIndex);
    } else if (!welcomeField) {
      setCurrentIndex(0);
    }
  }, [welcomeField, initialIndex]);

  const isWelcomeScreen = currentIndex === -1 && welcomeField !== null;
  const isFirstQuestion = currentIndex === 0;

  const currentItem = currentIndex >= 0 && currentIndex < totalQuestions
    ? navigableItems[currentIndex]
    : null;
  const currentField = currentItem?.field || null;
  const currentContextFields = currentItem?.contextFields || [];

  // Helper: find index of a field by id
  const findIndexByFieldId = useCallback(
    (fieldId: string): number => {
      return navigableItems.findIndex((item) => item.field.id === fieldId);
    },
    [navigableItems]
  );

  // ── Page-level state ──
  const resolvedPages = pages || [];
  const hasPages = resolvedPages.length > 0;
  const totalPages = resolvedPages.length;

  // Refs for branching maps — goNextPage may be called from stale closures
  // (e.g. auto-advance setTimeout) so it must read the latest values via refs.
  const nextPageMapRef = useRef(nextPageMap);
  nextPageMapRef.current = nextPageMap;
  const resolvedPagesRef = useRef(resolvedPages);
  resolvedPagesRef.current = resolvedPages;

  const currentPageIndex = useMemo(() => {
    if (!hasPages || !currentField) return 0;
    const idx = resolvedPages.findIndex((rp) => rp.fields.some((f) => f.id === currentField.id));
    return idx >= 0 ? idx : 0;
  }, [resolvedPages, currentField, hasPages]);

  const currentPage = hasPages && currentPageIndex >= 0 ? resolvedPages[currentPageIndex] : null;
  const currentFields = currentPage?.fields || (currentField ? [currentField] : []);
  const isMultiFieldPage = currentFields.length > 1;
  const isFirstPage = currentPageIndex === 0;
  // Story 5.7: when nextPageMap is available, the last page is one whose map entry is null.
  // Reads from ref to stay in sync with latest formData-derived map.
  // Última página = não há nenhuma página NAVEGÁVEL adiante (páginas só com
  // heading/paragraph não contam — seriam puladas por goToPageByIndex).
  const isLastPage = useMemo(() => {
    if (!hasPages) return false;
    const map = nextPageMapRef.current;
    const pages = resolvedPagesRef.current;
    let targetIdx = currentPageIndex + 1;

    if (map && currentPage) {
      const targetPageId = map[currentPage.page.id];
      if (targetPageId === null || targetPageId === undefined) return true;
      const mapped = pages.findIndex((rp) => rp.page.id === targetPageId);
      // Alvo fora das páginas visíveis → goNextPage cai no sequencial
      if (mapped >= 0) targetIdx = mapped;
    }

    return findNavigablePageIndex(pages, targetIdx) < 0;
  }, [hasPages, currentPage, currentPageIndex, totalPages, nextPageMap, resolvedPages]);

  const pageProgress = useMemo(() => {
    if (!hasPages || totalPages <= 0) return 0;
    if (calculatePageProgress && currentPage) {
      return Math.round(calculatePageProgress(currentPage.page.id).percentage);
    }
    return Math.round((Math.max(0, currentPageIndex) / totalPages) * 100);
  }, [hasPages, currentPageIndex, totalPages, calculatePageProgress, currentPage]);

  // Determine if this is the last question (considering pages + branching)
  const isLastQuestion = useMemo(() => {
    if (!currentField) return false;
    if (hasPages) return isLastPage;
    if (nextFieldMap) {
      return nextFieldMap[currentField.id] === null;
    }
    return currentIndex === totalQuestions - 1;
  }, [currentField, currentIndex, totalQuestions, nextFieldMap, hasPages, isLastPage]);

  // Progress: page-level when pages exist, else path-aware or sequential (AC2)
  const progress = useMemo(() => {
    if (hasPages) return pageProgress;
    if (calculateProgress && currentField) {
      return Math.round(calculateProgress(currentField.id).percentage);
    }
    return totalQuestions > 0
      ? Math.round((Math.max(0, currentIndex) / totalQuestions) * 100)
      : 0;
  }, [hasPages, pageProgress, calculateProgress, currentField, currentIndex, totalQuestions]);

  const effectiveTotalQuestions = useMemo(() => {
    if (hasPages) return totalPages;
    if (calculateProgress && currentField) {
      return calculateProgress(currentField.id).total;
    }
    return totalQuestions;
  }, [hasPages, totalPages, calculateProgress, currentField, totalQuestions]);

  const clearError = useCallback(() => {
    setValidationError(null);
  }, []);

  // Helper: jump to first navigable field of a page.
  // resolvePages() cria uma página por campo — inclusive heading/paragraph, que não
  // são navegáveis. Essas páginas não têm slide próprio (o heading é renderizado como
  // contexto da próxima pergunta), então são PULADAS no sentido da navegação; sem isso
  // o formulário travava silenciosamente na pergunta anterior a um heading.
  const goToPageByIndex = useCallback(
    (pageIdx: number, direction: 'forward' | 'backward' = 'forward'): boolean => {
      const idx = findNavigablePageIndex(resolvedPagesRef.current, pageIdx, direction);
      if (idx < 0) return false;
      const firstNav = resolvedPagesRef.current[idx].fields.find(
        (f) => !NON_NAVIGABLE_TYPES.includes(f.type)
      )!;
      const targetIndex = navigableItems.findIndex((item) => item.field.id === firstNav.id);
      if (targetIndex < 0) return false;
      setCurrentIndex(targetIndex);
      return true;
    },
    [navigableItems]
  );

  // ── goNextPage (AC1): page-aware forward ──
  const goNextPage = useCallback(() => {
    if (isComplete) return;

    // Welcome → first page
    if (currentIndex === -1) {
      setCurrentIndex(0);
      setValidationError(null);
      setPageHistory([]);
      return;
    }

    // Validate all fields on current page (sequential — stop at first error)
    if (isMultiFieldPage && currentPage) {
      for (const field of currentPage.fields) {
        if (NON_NAVIGABLE_TYPES.includes(field.type)) continue;
        const error = validateField(field);
        if (error) {
          setValidationError(error);
          onPageValidationError?.(field.id, error);
          return;
        }
      }
    } else if (currentField) {
      const error = validateField(currentField);
      if (error) {
        setValidationError(error);
        return;
      }
    }

    setValidationError(null);
    setPageHistory((prev) => [...prev, currentPageIndex]); // AC7

    // Story 5.7: branching-aware page navigation. Read from refs to avoid stale
    // closures (auto-advance setTimeout captures old goNextPage before re-render).
    const latestPageMap = nextPageMapRef.current;
    const latestPages = resolvedPagesRef.current;
    if (latestPageMap && currentPage) {
      const targetPageId = latestPageMap[currentPage.page.id];
      if (targetPageId === null || targetPageId === undefined) {
        onSubmit();
        return;
      }
      const targetIdx = latestPages.findIndex((rp) => rp.page.id === targetPageId);
      if (targetIdx >= 0) {
        // Nenhuma página navegável a partir do alvo (ex.: só headings até o fim) → fim do form
        if (!goToPageByIndex(targetIdx)) onSubmit();
        return;
      }
      // Target page id not found in resolved (filtered) pages → fall through to sequential
    }

    if (currentPageIndex >= totalPages - 1) {
      onSubmit();
      return;
    }

    if (!goToPageByIndex(currentPageIndex + 1)) onSubmit();
  }, [isComplete, currentIndex, isMultiFieldPage, currentPage, currentField, validateField, currentPageIndex, totalPages, onSubmit, goToPageByIndex, onPageValidationError]);

  // ── goPreviousPage (AC7): page-aware backward using history ──
  const goPreviousPage = useCallback(() => {
    if (isComplete) return;
    setValidationError(null);

    // AC7: "Back button always goes to last visited page"
    if (pageHistory.length > 0) {
      const prevPageIdx = pageHistory[pageHistory.length - 1];
      setPageHistory((prev) => prev.slice(0, -1));
      goToPageByIndex(prevPageIdx, 'backward');
      return;
    }

    if (currentPageIndex === 0 && welcomeField) {
      setCurrentIndex(-1);
      return;
    }

    if (currentPageIndex > 0) {
      // 'backward': pula páginas só com heading/paragraph rumo à pergunta anterior
      if (!goToPageByIndex(currentPageIndex - 1, 'backward') && welcomeField) {
        setCurrentIndex(-1);
      }
    }
  }, [isComplete, pageHistory, currentPageIndex, welcomeField, goToPageByIndex]);

  // ── Field-level goNext (backward compat — no pages, AC8) ──
  const goNextField = useCallback(() => {
    if (isComplete) return;

    if (currentIndex === -1) {
      setCurrentIndex(0);
      setValidationError(null);
      return;
    }

    if (currentField) {
      const error = validateField(currentField);
      if (error) {
        setValidationError(error);
        return;
      }
    }

    setValidationError(null);

    // Branching-aware navigation
    if (nextFieldMap && currentField) {
      const nextId = nextFieldMap[currentField.id];
      if (nextId) {
        setFieldHistory((prev) => [...prev, currentField.id]);
        const nextIndex = findIndexByFieldId(nextId);
        if (nextIndex >= 0) {
          setCurrentIndex(nextIndex);
        } else {
          onSubmit();
        }
      } else {
        onSubmit();
      }
      return;
    }

    if (currentIndex === totalQuestions - 1) {
      onSubmit();
      return;
    }

    setFieldHistory((prev) => currentField ? [...prev, currentField.id] : prev);
    setCurrentIndex((prev) => Math.min(prev + 1, totalQuestions - 1));
  }, [currentIndex, currentField, isComplete, totalQuestions, validateField, onSubmit, nextFieldMap, findIndexByFieldId]);

  // ── Field-level goPrevious (backward compat) ──
  const goPreviousField = useCallback(() => {
    if (isComplete) return;
    setValidationError(null);

    if (fieldHistory.length > 0) {
      const prevId = fieldHistory[fieldHistory.length - 1];
      setFieldHistory((prev) => prev.slice(0, -1));
      const prevIndex = findIndexByFieldId(prevId);
      if (prevIndex >= 0) {
        setCurrentIndex(prevIndex);
        return;
      }
    }

    if (currentIndex === 0 && welcomeField) {
      setCurrentIndex(-1);
      return;
    }

    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, [currentIndex, isComplete, welcomeField, fieldHistory, findIndexByFieldId]);

  // Unified goNext / goPrevious: page-level when pages exist, field-level otherwise
  const goNext = hasPages ? goNextPage : goNextField;
  const goPrevious = hasPages ? goPreviousPage : goPreviousField;

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalQuestions) {
        setValidationError(null);
        setCurrentIndex(index);
      }
    },
    [totalQuestions]
  );

  // AC3/AC4: auto-advance only for single-field pages with selection types
  const shouldAutoAdvance = useCallback(
    (fieldType: FieldType): boolean => {
      if (isMultiFieldPage) return false; // AC4
      return AUTO_ADVANCE_TYPES.includes(fieldType); // AC3
    },
    [isMultiFieldPage]
  );

  // Teclado (mandato user 2026-08-14):
  //   Ctrl+Enter / Cmd+Enter → avança em QUALQUER campo, inclusive texto longo
  //   Enter puro            → avança, EXCETO em textarea/contenteditable, onde
  //                           quebra linha (era o conflito relatado)
  //   Shift+Enter           → volta (fora de campos de texto longo)
  // Tab/Shift+Tab dentro da página seguem nativos (AC5).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;

      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        goNext();
        return;
      }

      const inLongText =
        e.target instanceof HTMLTextAreaElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable);
      if (inLongText) return;

      e.preventDefault();
      if (e.shiftKey) goPrevious();
      else goNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrevious]);

  return {
    currentIndex,
    currentField,
    currentContextFields,
    totalQuestions: effectiveTotalQuestions,
    progress,
    isFirstQuestion,
    isLastQuestion,
    isWelcomeScreen,
    isComplete,
    goNext,
    goPrevious,
    goToIndex,
    validationError,
    clearError,
    // Page-level API
    currentPageIndex,
    currentPage,
    currentFields,
    totalPages,
    pageProgress,
    isFirstPage,
    isLastPage,
    isMultiFieldPage,
    goNextPage,
    goPreviousPage,
    shouldAutoAdvance,
  };
}
